<script>
/**
 * Asset Management
 */
import { mapGetters } from "vuex";
import MenuWidget from "./MenuWidget.vue";

export default {
    name: "DefaultMenu",
    components: { MenuWidget },
    computed: {
        ...mapGetters("auth", ["getIsAdmin", "getAclRole", "getAuthUser"]),
        showServices() {
            if (
                this.$isDK &&
                (this.getAuthUser.department_id == 16 ||
                    this.getAuthUser.department_id == 21 ||
                    this.getAuthUser.id == 27)
            ) {
                return true;
            } else {
                return false;
            }
        }
    },
    methods: {
        async doLogout() {
            await this.$store.dispatch("auth/logout");
            this.$router.push("/login");
        },
        isAdmin() {
            if (this.getAclRole === "ROOT") {
                return true;
            } else {
                return false;
            }
        },
        isLeader() {
            if (this.getAclRole === "LEADER" || this.getAclRole === "ROOT") {
                return true;
            } else {
                return false;
            }
        }
    }
};
</script>

<template>
    <side-bar>
        <template slot="links">
            <!-- registration -->
            <sidebar-item
                :link="{
                    name: $t('menu.registration'),
                    icon: 'far fa-cabinet-filing'
                }"
                :force-navigation="{ name: 'StartDashboard' }"
            >
                <sidebar-item
                    :link="{
                        name: $t('menu.my_cases_overview'),
                        path: '/projects/my-overview'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.all_cases'),
                        path: '/projects/index'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.my_cases_invoices'),
                        path: '/projects/my-invoices'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.my_tasks'),
                        icon: 'far fa-tasks',
                        path: '/tasks/my-tasks'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.my_travels'),
                        icon: 'far fa-cars',
                        path: '/travels/my-travels'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.my_hours'),
                        icon: 'far fa-hourglass',
                        path: '/reports/my-hours'
                    }"
                />
            </sidebar-item>
            <!-- .registration -->

            <!-- Extra functions -->
            <sidebar-item
                v-if="$aclCan($ACL.CUSTOM_ACCESS_CONTRACTS)"
                :link="{
                    name: $t('menu.extra-functions'),
                    icon: 'far fa-stars'
                }"
            >
                <sidebar-item
                    :link="{
                        name: $t('menu.contract_contract_index'),
                        path: '/contracts/index'
                    }"
                    v-if="$aclCan($ACL.CUSTOM_ACCESS_CONTRACTS)"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.contract_contract_terminations'),
                        path: '/contracts/terminations'
                    }"
                    v-if="$aclCan($ACL.CUSTOM_ACCESS_CONTRACTS)"
                />
            </sidebar-item>

            <!-- Own assets -->
            <sidebar-item
                :link="{
                    name: $t('menu.own_assets'),
                    path: $aclCan($ACL.CUSTOM_FULL_MB)
                        ? '/reports/mb-report/dk-assets-full'
                        : '/reports/mb-report/dk-assets',
                    icon: 'far fa-wind-turbine'
                }"
            />
            <sidebar-item
                :link="{
                    name: $t('menu.statistics'),
                    path: '/reports/mb-report/spot-prices',
                    icon: 'far fa-chart-pie'
                }"
            />

            <!-- Service -->
            <sidebar-item
                v-if="showServices"
                :link="{
                    name: $t('menu.service'),
                    icon: 'far fa-wrench'
                }"
            >
                <sidebar-item
                    :link="{
                        name: $t('menu.mes_assets'),
                        path: '/reports/mb-report/mes-assets'
                    }"
                ></sidebar-item>

                <sidebar-item
                    :link="{
                        name: $t('menu.windmill_stoplist'),
                        path: '/projects/turbine-stops'
                    }"
                >
                </sidebar-item>
            </sidebar-item>

            <!-- Bookkeeping -->
            <sidebar-item
                :link="{ name: $t('menu.bookkeepers'), icon: 'far fa-book' }"
                :hide-mobile="true"
                v-if="isAdmin()"
            >
                <sidebar-item
                    :link="{
                        name: $t('menu.travel_viewer'),
                        icon: 'far fa-tasks',
                        path: '/travels/index'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.travel_salary'),
                        icon: 'far fa-tasks',
                        path: '/travels/salary'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.travel_salary_reports'),
                        icon: 'far fa-tasks',
                        path: '/travels/salary-reports'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.global_inbox'),
                        path: '/vouchers/inbox'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.global_booked_costs'),
                        path: '/vouchers/index'
                    }"
                />

                <sidebar-item
                    :link="{
                        name: $t('menu.economic_connections'),
                        path: '/ec-connections'
                    }"
                />
            </sidebar-item>

            <!-- Settings -->
            <sidebar-item
                :link="{
                    name: $t('menu.administration'),
                    icon: 'far fa-cogs',
                    path: '/settings'
                }"
                v-if="isAdmin()"
            >
                <sidebar-item
                    :link="{
                        name: $t('menu.activities'),
                        path: '/settings/activities'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.milestones'),
                        path: '/settings/milestones'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.role_prices'),
                        path: '/settings/role-prices'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.travel_prices'),
                        path: '/travel-prices'
                    }"
                />
                <sidebar-item
                    :link="{ name: $t('menu.users'), path: '/users/index' }"
                    :hide-mobile="true"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.departments'),
                        path: '/departments/index'
                    }"
                    :hide-mobile="true"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.reopen_day'),
                        icon: 'far fa-tasks',
                        path: '/projects/reopen'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.currencies'),
                        path: '/contracts/currencies'
                    }"
                    :hide-mobile="true"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.netto_price_index'),
                        path: '/contracts/netprices'
                    }"
                    :hide-mobile="true"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.asset_categories'),
                        path: '/contracts/asset-groups'
                    }"
                    :hide-mobile="true"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.contract_products'),
                        path: '/contracts/products'
                    }"
                    :hide-mobile="true"
                />
                <template v-if="$TENANT === 'dk'">
                    <sidebar-item
                        :link="{
                            name: $t('menu.import_customers'),
                            path: '/customers/import'
                        }"
                        :hide-mobile="true"
                    />
                </template>
            </sidebar-item>

            <!-- reports -->
            <sidebar-item
                :link="{ name: $t('menu.reports'), icon: 'far fa-chart-pie' }"
                :hide-mobile="true"
                v-if="isLeader()"
            >
                <sidebar-item
                    :link="{
                        name: $t('menu.all_customers'),
                        path: '/customers/index'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.all_tasks'),
                        icon: 'far fa-tasks',
                        path: '/tasks/index'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.all_cases_overview'),
                        icon: 'far fa-tasks',
                        path: '/projects/full-overview'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.projects_invoicing_report'),
                        icon: 'far fa-tasks',
                        path: '/projects/invoicing-report'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.hour_report'),
                        path: '/reports/activities'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.absence'),
                        path: '/reports/absence'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.cases'),
                        path: '/reports/projects'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.cases_performance'),
                        path: '/reports/projects-performance'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.open_days'),
                        path: '/reports/open-days'
                    }"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.contract_reports'),
                        path: '/reports/contracts'
                    }"
                    :hide-mobile="true"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.personal_multiview'),
                        path: '/reports/personal-multiview'
                    }"
                    :hide-mobile="true"
                />
                <sidebar-item
                    :link="{ name: $t('menu.budget'), path: '/reports/budget' }"
                    :hide-mobile="true"
                />
            </sidebar-item>

            <!-- extra functions -->
            <sidebar-item
                v-if="
                    $aclCan($ACL.CUSTOM_PLANNING_EXP_DEVMODEL) ||
                        $aclCan($ACL.CUSTOM_PLANNING_IMPORT_BUDGETS)
                "
                :link="{
                    name: $t('menu.extra-functions'),
                    icon: 'far fa-stars'
                }"
            >
                <sidebar-item
                    :link="{
                        name: $t('menu.data_exports'),
                        path: '/reports/projects-reports'
                    }"
                    v-if="$aclCan($ACL.CUSTOM_PLANNING_EXP_DEVMODEL)"
                />

                <sidebar-item
                    :link="{
                        name: $t('menu.import_project_budgets'),
                        path: '/projects/import-budgets'
                    }"
                    v-if="$aclCan($ACL.CUSTOM_PLANNING_IMPORT_BUDGETS)"
                />
                <sidebar-item
                    :link="{
                        name: $t('menu.planning_report'),
                        path: '/projects/planning-report'
                    }"
                    v-if="$aclCan($ACL.CUSTOM_PLANNING_EXP_DEVMODEL)"
                />
            </sidebar-item>

            <!-- Always present -->
            <sidebar-item
                :link="{
                    name: $t('menu.help'),
                    icon: 'far fa-question-circle',
                    path: '/help'
                }"
            >
            </sidebar-item>
            <a class="nav-link" href="#" @click.prevent="doLogout">
                <i class="far fa-sign-out"></i>
                <span class="nav-link-text">{{ $t("menu.logout") }}</span>
            </a>
            <!-- .Always present -->
        </template>
    </side-bar>
</template>
